import { useState } from "react";
import { useDispatch } from "react-redux";
import { useWorkspace } from "@/common";
import Icon from "@imminently/decisively-ui/Icon";
import Avatar from "@imminently/decisively-ui/Avatar";

import { useAddWorkspaceModal, useBrowseWorkspacesModal } from "@/features/workspace";
import { setWorkspace, useScope } from "../../redux/scope";
import { workspaceService } from "@/app/providers/services";
import { ImmiCombobox } from "@/components/radix/UserSelect/AsyncSelect";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@/ui/primitives/skeleton";
import { cn } from "@/lib/utils";

const WorkspaceSelectorSkeleton = ({ collapsed }: { collapsed?: boolean }) => (
  <div className={cn("flex items-center justify-start gap-3 w-full rounded-md border-none bg-transparent text-left p-1")}>
    <Skeleton className="w-8 h-8 rounded-md shrink-0" />
    {
      !collapsed ? (
        <div className="flex-1 min-w-0">
          <Skeleton className="w-full h-4 rounded-md" />
          <Skeleton className="w-3/4 h-3 mt-1 rounded-md" />
        </div>
      ) : null
    }
  </div>
);

export const WorkspaceSelector = ({ collapsed }: { collapsed?: boolean }) => {
  const dispatch = useDispatch();
  const { workspace: workspaceId } = useScope();
  const workspace = useWorkspace(workspaceId);
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const openAdd = useAddWorkspaceModal();
  const openBrowse = useBrowseWorkspacesModal();

  const updateWorkspace = (id: string) => {
    dispatch(setWorkspace(id));
  };

  // if we dont have a workspace id or the workspace object, render the skeleton loader
  if (!workspaceId || !workspace) {
    return <WorkspaceSelectorSkeleton collapsed={collapsed} />;
  }

  const comboProps = {
    label: t('workspaces.title'),
    service: workspaceService,
    side: "right" as const,
    open,
    setOpen,
    value: workspaceId,
    onSelect: (item) => updateWorkspace(item.id),
    renderItem: (item) => (
      <>
        <Avatar size="md" name={item.name} className="rounded-md" />
        <span className="block truncate">{item.name}</span>
      </>
    ),
    actions: {
      primary: {
        name: t('browse_all'),
        icon: <Icon name="browse-all" />,
        type: 'button' as const,
        variant: 'ghost' as const,
        intent: 'secondary' as const,
        size: 'sm' as const,
        onClick: () => {
          openBrowse();
        },
      },
      secondary: [{
        name: t('workspaces.create'),
        icon: <Icon name="add" />,
        type: 'button' as const,
        variant: 'ghost' as const,
        size: 'sm' as const,
        onClick: () => {
          openAdd();
        },
      }],
    }
  };

  return (
    <ImmiCombobox {...comboProps}>
      <button className={cn(
        "flex items-center justify-start gap-3 rounded-md text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors bg-transparent cursor-pointer text-left p-1",
        collapsed ? "w-fit" : "w-full",
      )}>
        <Avatar size="md" name={workspace.name} className="text-primary-foreground bg-primary rounded-md" />
        {
          !collapsed ? (
            <>
              <div className="flex-1 min-w-0 h-8">
                <div className="font-semibold text-sm leading-4 truncate">
                  {workspace.name}
                </div>
                <div className="text-xs text-muted-light leading-4">
                  {t('workspaces.workspace')}
                </div>
              </div>
              <Icon name="resize" className="text-muted-light shrink-0" />
            </>
          ) : null
        }
      </button>
    </ImmiCombobox>
  );
};
